// selectors for the individual Column components of a Sheet or ResourceTable
import Immutable, { fromJS, List as ImmutableList } from "immutable"
import { isWebUri } from "valid-url"
import { createSelector, createStructuredSelector } from "reselect"

// selectors
import {
    makeResourceTableConfigSelector,
    selectCanCustomColorColumns as selectSheetCanCustomColorColumns,
    selectCanHideColumns,
    selectCanRemoveColumns,
    selectCanSetDefaultSort,
    selectCellStates,
    selectCellStateToStyleProp,
    selectClickedColumn,
    selectClickedRow,
    selectColumns,
    selectDefaultSortColumnIndex,
    selectDefaultSortColumnType,
    selectFilteredAndSortedRowData,
    selectFocusedColumnIndex,
    selectIsPublicSheetMode,
    selectIsReorderable,
    selectIsResourceTable,
    selectOptionsForCustomTagCell,
    selectRowHeight,
    selectRowHeightFactor,
    selectSearchingColumnIndexes,
    selectSheetCanFitInMemory,
    selectSheetDataType,
    selectSheetId,
    selectSheetIsLoading,
    selectSheetRowsTotalCount,
    selectSortingColumnIndex,
    selectSortType,
    selectStoredSheetDataType,
    selectTagTypeForCustomTagCell,
    showDatumColumn,
    selectShouldDisplayMathBar,
    selectRowData,
} from "sheets/selectors/sheet-selectors"
import { selectCellLink } from "sheets/components/cells/cell-selectors/datum-cell-selectors"
import {
    selectIsOrgAdmin,
    selectShouldLimitEditingNotesToAdminsAndCreators,
    selectUserId,
} from "shared/userdata/userdata-selectors"
import { centsToCurrencyStr } from "shared/pac/helperFunctions"

// helper functions
import { computeColumnData } from "sheets/switches"
import { calculateTextWidth } from "imports/styleHelperFunctions"
import { getNestedObjectKey, parseUrl, decodeStyles, encodeStyles } from "shared/imports/sharedHelperFunctions"
import hideIfTrue from "imports/hideIfTrue"
import { processCellValue, getNullValueForColumn, computeLeftBoundRange } from "sheets/helper-functions"
import { checkHasRegion } from "shared/imports/permissionFunctions"

// constants
import sheetConstants from "sheets/constants"
import { allRegionsExceptUS } from "shared/imports/regionConstants"

// styles
import sheetVars from "app/static/stylus/sheets-variables.json"
const sheetsVariables = decodeStyles(sheetVars)

export const selectColumnIndexFromProps = (_, props) => props.columnIndex

export const selectRowDataFromProps = (_, props) => props.rowData

export const selectRowCountFromProps = (_, props) => props.rowCount

const fakeDatumColumn = Immutable.fromJS({
    columnType: undefined,
})
export const selectPropFromProps = (propName) => (_, props) => props[propName]

export const selectIndividualColumnByIndex = createSelector(
    selectColumns,
    selectColumnIndexFromProps,
    (columns, columnIndex) => {
        if (columns && columns.size) {
            if (columnIndex === sheetConstants.DATUM_COLUMN_INDEX) {
                return fakeDatumColumn
            } else {
                return columns.get(columnIndex)
            }
        }
    },
)

export const selectColumnForComponent = createSelector(
    selectIndividualColumnByIndex,
    (column) => column && column.toJS(),
)

// select a particular property within the column Map
// it is possible to provide a defaultValue that is return when property
// can not be found
export const makeColumnPropertySelector = (property, defaultValue) =>
    createSelector(selectIndividualColumnByIndex, (column) => column && column.get(property, defaultValue))

// select the column id for sheets and the itteration index for ResourceTables
// Migration Note: this was the logic in Sheet.jsx
export const selectColumnId = createSelector(
    makeColumnPropertySelector("id"),
    selectColumnIndexFromProps,
    (columnId, columnIndex) => columnId || columnIndex,
)

/**
 * Select for determining if the column is after index column
 *
 * @returns {Boolean}
 */
export const selectIsIndexColumn = createSelector(
    selectColumnIndexFromProps,
    (columnIndex) => columnIndex === sheetConstants.INDEX_COLUMN,
)

/**
 * Select for determining if the column is a datum column, the fixed left-most columns
 * the source data for which is chosen from the QuorumDataType.display_key for the sheet's data type
 *
 * @returns {Boolean}
 */
export const selectIsDatumColumn = createSelector(
    selectColumnIndexFromProps,
    (columnIndex) => columnIndex === sheetConstants.DATUM_COLUMN_INDEX,
)

// ------ BEGIN selectors that index in to a column Immutable Map -------------- //

export const selectCanSortOnBackend = makeColumnPropertySelector("can_sort_on_backend")

export const selectCanFilterOnBackend = makeColumnPropertySelector("can_filter_on_backend")

export const selectColumnName = createSelector(
    makeColumnPropertySelector("name"),
    makeColumnPropertySelector("source_saved_search_data"),
    (name, data) => {
        if (data && data.get("data_type") === DjangIO.app.models.QuorumDataType.bulk_email.value) {
            return `${name} in last 12 months`
        }
        return name
    },
)
export const selectColumnSum = makeColumnPropertySelector("custom_data_sums")

export const selectColumnNameTooltip = makeColumnPropertySelector("name_tooltip")

// The column type is always populated with a value of type SheetColumnType
// For ResourceTables, we default to the enum SheetColumnType.field to allow
// us to configure ResourceTable columns
export const selectColumnType = makeColumnPropertySelector(
    "column_type",
    DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value,
)

// for field columns, get the field, a string representing a field on a model
export const selectColumnField = makeColumnPropertySelector("field")

export const selectProjectStatType = makeColumnPropertySelector("project_stat_type")

export const getCustomColorSettings = makeColumnPropertySelector("custom_color_settings", Immutable.fromJS({}))

export const selectCustomTag = makeColumnPropertySelector("custom_tag")

export const selectResourceTableOptionalActionCreator = makeColumnPropertySelector("optionalActionCreator")

export const selectResourceTableOptionalDisplayFunction = makeColumnPropertySelector("display")

export const selectShouldDisplayAsCurrency = makeColumnPropertySelector("should_force_display_as_currency", false)

export const selectShouldRoundCellValue = makeColumnPropertySelector("should_round_cell_value", false)

export const selectColumnHasError = makeColumnPropertySelector("error", false)

export const selectColumnCustomErrorMessage = makeColumnPropertySelector("custom_error_message", "")

export const selectBigSheetColumnRange = makeColumnPropertySelector("range")

export const selectResourceTableFilterOn = makeColumnPropertySelector("resourceTableFilterOn")

export const selectResourceTableSortOn = makeColumnPropertySelector("resourceTableSortOn")

export const selectResourceTableSearchTrigger = makeColumnPropertySelector("resourceTableSearchTrigger")

export const selectResourceTableDisableCellLink = makeColumnPropertySelector("resourceTableDisableCellLink")

export const selectResourceTableEnableEditing = makeColumnPropertySelector("resourceTableEnableEditing")

export const selectColumnSortingQuerymethodFromColumn = makeColumnPropertySelector("columnSortingQuerymethod")

// ------ END selectors that index in to a column Immutable Map -------------- //

export const selectColumnSearchField = createSelector(
    selectIsResourceTable,
    makeColumnPropertySelector("field"),
    makeColumnPropertySelector("resourceTableFilterOn"),
    (isResourceTable, field, resourceTableFilter) => (isResourceTable && resourceTableFilter) || field,
)

export const selectFilteredAndSortedDataForComponent = createSelector(
    selectFilteredAndSortedRowData,
    (filteredData) => filteredData && filteredData.toJS(),
)

/**
 * This function finds the field object via DjangIO from the column field string stored in the Column
 * @name selectFieldObject
 * @function
 * @param {() => String} selectColumnField - a string representation of the field on a model this column represents
 *  Can be null
 * @param {() => Integer} selectStoredSheetDataType - The data type of the sheet
 *
 * @returns {Object} - DjangIO Serialized version of the Django field
 */
export const selectFieldObject = createSelector(
    selectColumnField,
    selectStoredSheetDataType,
    (columnField, dataType) => DjangIO.findField(columnField, dataType) || {},
)

export const selectCustomColorSettings = createSelector(
    getCustomColorSettings,
    (customColorSettings) => customColorSettings && customColorSettings.toOrderedMap(),
)

export const selectFieldObjectForSearch = createSelector(
    selectColumnSearchField,
    selectStoredSheetDataType,
    (columnField, dataType) => DjangIO.findField(columnField, dataType) || {},
)

// get the column data type of the column
// most of the time we'll get it from the automatic type system that Jake has created
// provide the ability to override this via the PublicField serialization
// for resource tables, the column data type defaults to field, which will allow
// for some type introspection
export const selectColumnDataType = createSelector(
    selectFieldObject,
    makeColumnPropertySelector(
        "column_data_type",
        DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.field.value,
    ),
    (field, columnDataType) => (field && field.overriden_column_data_type) || columnDataType,
)

export const selectIsAverageProjectStat = createSelector(
    selectColumnType,
    selectColumnField,
    selectProjectStatType,
    (columnType, field, projectStatType) =>
        columnType === DjangIO.app.userdata.spreadsheet.types.SheetColumnType.project_stats.value &&
        (projectStatType === DjangIO.app.userdata.types.ProjectStatType.average.value || field === "project_score"),
)

export const selectColumnData = createSelector(
    selectIndividualColumnByIndex,
    selectColumns,
    (column, allColumns) => column && allColumns && computeColumnData(column, allColumns),
)

// get the range of the column data, used for StatCell as well as filtering component
// for numbers
export const selectColumnRange = createSelector(
    (state, props) => selectBigSheetColumnRange(state, props),
    (state, props) => selectColumnData(state, props),
    (bigSheetColumnRange, columnData) => {
        // if we are a big sheet, we should have a range passed from the backend
        if (bigSheetColumnRange) {
            return bigSheetColumnRange.toJS()
        }
        if (columnData && columnData.size) {
            // silly way we have to check if the object is empty
            const topRange = columnData.map(Number).filter(Boolean).max() || 100
            const lowerRange = columnData.map(Number).filter(Boolean).min() || 0
            return [
                lowerRange === -Infinity ? Number.MIN_SAFE_INTEGER : lowerRange,
                topRange === Infinity ? Number.MAX_SAFE_INTEGER : topRange, // make sure we don't send Infinity
            ]
        } else {
            return [0, 0]
        }
    },
)

// returns null for Resource Tables
// this selector should be used to provide the co
export const selectColumnDataForColumn = createSelector(
    (state, props) => selectColumnData(state, props),
    (columnData) => columnData && columnData.toJS(),
)

export const selectTransformFunctionForDisplayCell = createSelector(selectColumnType, (columnType) => {
    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_bill.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_person.value:
            return (region) => region && String(region).toUpperCase()
        default:
            return (identity) => identity
    }
})

// Column Permission Selectors
export const selectIsResourceTableEditable = createSelector(
    selectIsResourceTable,
    selectIndividualColumnByIndex,
    (isResourceTable, resourceTableColumn) =>
        isResourceTable && resourceTableColumn ? resourceTableColumn.get("resourceTableEnableEditing", false) : false,
)
export const selectColumnIsEditable = createSelector(
    selectFieldObject,
    selectColumnType,
    selectIsPublicSheetMode,
    selectIsAverageProjectStat,
    selectIsResourceTable,
    selectIsResourceTableEditable,
    (fieldObj, columnType, isPublicSheetMode, isAverageProjectStat, isResourceTable, isResourceTableColumnEditable) => {
        if (isResourceTable) {
            return isResourceTableColumnEditable
        }
        if (isPublicSheetMode || isAverageProjectStat) {
            return false
        }

        // either we enabled editing on the SheetColumnType enum, or we enabled editing in the PublicField system
        if (DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).editable || fieldObj.patch) {
            return true
        }
        return false
    },
)

export const selectShouldShowEditableIcon = createSelector(
    selectIsResourceTable,
    selectColumnIsEditable,
    (isResourceTable, columnIsEditable) => isResourceTable && columnIsEditable,
)

// TODO: implement permissioning on enum for Tag Types and Field Columns
export const selectIsColoringSupportedForColumnDataType = createSelector(
    selectColumnDataType,
    selectTagTypeForCustomTagCell,
    selectFieldObject,
    selectColumnType,
    (columnDataType, tagType, fieldObject, columnType) => {
        // The column type itself may disallow coloring
        if (
            columnType &&
            !DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).coloring_enabled
        ) {
            return false
        }
        // if a custom field, switch on the tagType instead of the columnDataType
        // custom tags pass their slugs as the cellValues
        if (tagType) {
            switch (tagType) {
                case DjangIO.app.person.types.TagType.single_option_list.value:
                case DjangIO.app.person.types.TagType.string.value:
                case DjangIO.app.person.types.TagType.number.value:
                    return true
                default:
                    // multi_option_list is unsupported because the behavior cannot be determined
                    return false
            }
        } else if (fieldObject && Object.keys(fieldObject).length) {
            if (fieldObject._is_enum_field) {
                return true
            }
            switch (fieldObject.field_type) {
                case DjangIO.djangio.types.FieldType.CharField.value:
                case DjangIO.djangio.types.FieldType.TextField.value:
                case DjangIO.djangio.types.FieldType.IntegerField.value:
                case DjangIO.djangio.types.FieldType.FloatField.value:
                case DjangIO.djangio.types.FieldType.DecimalField.value:
                case DjangIO.djangio.types.FieldType.DateField.value:
                    return true
                default:
                    return false
            }
        } else {
            switch (columnDataType) {
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value:
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
                    return true
                default:
                    return false
            }
        }
    },
)

/**
 * This selector determines if a column is allowed to be custom colored in this context
 * @name selectCanCustomColorColumn
 * @function
 * @param {Boolean} sheetSupportsColor - whether or not this sheet can be custom colored
 * @param {Boolean} columnTypeSupportsColor - whether or not this particular column's data type supports coloring
 * @returns {Boolean} - whether or not colors may be applied to this column, regardless of if there is a custom coloring
 */
export const selectCanCustomColorColumn = createSelector(
    selectSheetCanCustomColorColumns,
    selectIsColoringSupportedForColumnDataType,
    (sheetSupportsColor, columnTypeSupportsColor) => sheetSupportsColor && columnTypeSupportsColor,
)

/**
 * This selector determines if we should custom color a column
 * @name selectShouldCustomColorColumn
 * @function
 * @param {Boolean} canCustomColorColumn - whether or not this column is allowed to be colored
 * @param {Map} customColorSettings - the custom color mapping for this column
 * @returns {Boolean} - whether or not colors should be applied to this column when rendering
 */
export const selectShouldCustomColorColumn = createSelector(
    selectCanCustomColorColumn,
    selectCustomColorSettings,
    (canCustomColorColumn, customColorSettings) =>
        canCustomColorColumn && Boolean(customColorSettings) && Boolean(customColorSettings.size),
)

export const selectIsResizable = selectCanRemoveColumns

// some of the selectors have the same permissions
export const selectCanAddColumns = createSelector(selectCanRemoveColumns, (canRemoveColumns) => canRemoveColumns)
export const selectCanRenameColumns = createSelector(selectCanRemoveColumns, (canRemoveColumns) => canRemoveColumns)

// UI Selectors

// determine if the current column is the one searching/filtering
export const selectIsColumnSearching = createSelector(
    selectSearchingColumnIndexes,
    selectColumnIndexFromProps,
    (searchingColumnIndexes, columnIndex) => searchingColumnIndexes && searchingColumnIndexes.includes(columnIndex),
)

export const selectIsDerived = createSelector(
    selectColumnType,
    (columnType) => columnType && DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).derived,
)

export const selectIsColumnFocused = createSelector(
    selectFocusedColumnIndex,
    selectColumnIndexFromProps,
    (focusedColumnIndex, columnIndex) => focusedColumnIndex === columnIndex,
)

export const selectIsColumnSorting = createSelector(
    (state, props) => selectSortingColumnIndex(state, props),
    (state, props) => selectColumnIndexFromProps(state, props),
    (sortingColumnIndex, columnIndex) => sortingColumnIndex === columnIndex,
)

export const selectIsColumnDefaultSorting = createSelector(
    selectDefaultSortColumnIndex,
    selectColumnIndexFromProps,
    (defaultSortingColumnIndex, columnIndex) => defaultSortingColumnIndex === columnIndex,
)

export const selectIsDefaultSortingAndCanSetDefaultSort = createSelector(
    selectIsColumnDefaultSorting,
    selectIsColumnSorting,
    selectCanRemoveColumns,
    (isDefaultSorting, isSorting, canRemove) => isDefaultSorting && !isSorting && canRemove,
)

// ------------------ BEGIN stat cell selectors -------------------------- //
// these need to be defined here because of circular import issues
export const selectDomainForStatCell = createSelector(
    (state, props) => selectColumnRange(state, props),
    (columnRange) => columnRange || [0, 0],
)

export const selectScaleForStatCell = createSelector(
    (state, props) => selectDomainForStatCell(state, props),
    (domain) => d3.scale.linear().domain(domain).range([0, 100]),
)

export const selectCellValueForStatCell = createSelector(
    (state, props) => props.children,
    (state, props) => props.isRenderingPercent,
    (cellValue, isRenderingPercent) => {
        if (cellValue && isRenderingPercent) {
            return cellValue / 100
        }
        if (cellValue) {
            return cellValue
        }
    },
)

export const selectBarWidth = createSelector(
    (state, props) => selectCellValueForStatCell(state, props),
    (state, props) => selectScaleForStatCell(state, props),
    (cellValue, scale) => {
        if (Number.isNaN(cellValue) || cellValue === Infinity) {
            return
        }
        if (cellValue && scale) {
            return `${Math.max(1, scale(Number(cellValue)))}%`
        }
    },
)

/** @type {(state?: any, props?: any) => any} */
export const statCellConnection = createStructuredSelector({
    barWidth: (state, props) => selectBarWidth(state, props),
    domain: (state, props) => selectDomainForStatCell(state, props),
    scale: (state, props) => selectScaleForStatCell(state, props),
    shouldRoundCellValue: (state, props) => selectShouldRoundCellValue(state, props),
    shouldDisplayAsCurrency: (state, props) => selectShouldDisplayAsCurrency(state, props),
})

// ------------------ END stat cell selectors -------------------------- //

/** @type {(state?: any, props?: any) => any} */
export const numberCounterCellConnection = createStructuredSelector({
    columnList: (state, props) => selectSortedColumnValues(state, props),
    opacity: (state, props) => selectCircleCounterOpacity(state, props),
})

// used to grab the values for cells
export const selectChildrenFromProps = (_, props) => props && props.children

export const selectHasMultipleLinks = createSelector(selectChildrenFromProps, (children) =>
    ImmutableList.isList(children),
)

// Parse the link(s) for LinkCells
export const selectLink = createSelector(
    selectHasMultipleLinks,
    selectChildrenFromProps,
    (hasMultipleLinks, children) =>
        hasMultipleLinks
            ? children.map((link) =>
                  fromJS({
                      url: parseUrl(link.get("url")),
                      label: link.get("label"),
                  }),
              )
            : parseUrl(children),
)

// determine whether the link provided is valid
// will return false
export const selectIsValidLink = createSelector(
    selectHasMultipleLinks,
    selectLink,
    (hasMultipleLinks, link) => !hasMultipleLinks && isWebUri(link),
)

/** @type {(state?: any, props?: any) => any} */
export const linkCellConnection = createStructuredSelector({
    hasMultipleLinks: selectHasMultipleLinks,
    isValidLink: selectIsValidLink,
    link: selectLink,
})

// ------------------ BEGIN CELL SELECTORS ------------------- //
// sheets can only have their rows resized if we aren't in public sheets mode and are not resource table
export const selectCanResizeRows = createSelector(
    selectIsPublicSheetMode,
    selectIsResourceTable,
    (isPublicSheetMode, isResourceTable) => !isPublicSheetMode && !isResourceTable,
)

export const selectDatumColumnIsSearching = createSelector(
    selectSearchingColumnIndexes,
    (searchingColumnIndexes) =>
        searchingColumnIndexes && searchingColumnIndexes.includes(sheetConstants.DATUM_COLUMN_INDEX),
)

export const selectCustomFieldOptions = createSelector(
    selectChildrenFromProps,
    (children) =>
        (children = children && children.toJS && children.constructor === Immutable.List ? children.toJS() : children),
)

// Get all of the custom field options for the selector to display in its dropdown
export const selectAllCustomFieldOptionsForSelect = createSelector(
    selectOptionsForCustomTagCell,
    selectTagTypeForCustomTagCell,
    (allOptions, tagType) => {
        if (tagType === DjangIO.app.person.types.TagType.boolean.value) {
            return [true, false, ""]
        } else {
            return allOptions && allOptions.toJS && allOptions.constructor === Immutable.List
                ? allOptions.toJS()
                : allOptions
        }
    },
)

export const selectCustomFieldOptionsForDisplay = createSelector(
    selectCustomTag,
    selectCustomFieldOptions,
    (customTag, values) => {
        const getValue = (key) => key && customTag.getIn(["options_new", key, "value"])
        const isArchived = (key) => key && customTag.getIn(["options_new", key, "archived"])
        return values && values.constructor === Array
            ? values
                  .filter((each) => !isArchived(each))
                  .map((each) => getValue(each))
                  .join(", ")
            : getValue(values)
    },
)

export const selectResourceTableSwalConfig = createSelector(
    makeResourceTableConfigSelector("swalConfig"),
    (swalConfig) => swalConfig && swalConfig.toJS(),
)

export const selectRowIndexFromProps = (_, props) => props.rowIndex

// this selector will NOT work for public sheets, since the row data is filtered differently
export const selectDatumId = createSelector(
    selectFilteredAndSortedRowData,
    selectRowIndexFromProps,
    (rowData, rowIndex) => rowData && (rowIndex || rowIndex === 0) && rowData.get(rowIndex).get("id"),
)

// TODO: Fix this for public sheets
export const selectRowForCell = createSelector(
    selectRowData,
    selectFilteredAndSortedRowData,
    selectRowIndexFromProps,
    (state, props) => props.isMantineSheet,
    (rowData, filteredAndSortedRowData, rowIndex, isMantineSheet) => {
        if (isMantineSheet) {
            return rowData && rowData.get(rowIndex)
        }

        return filteredAndSortedRowData && filteredAndSortedRowData.get(rowIndex)
    },
)

export const selectRowIdForCell = createSelector(selectRowForCell, (row) => row && row.get("id"))

//----------- BEGIN UI Selectors ------------- //
export const selectIsClickedRow = createSelector(
    selectClickedRow,
    selectRowIdForCell,
    (clickedRow, rowId) => clickedRow === rowId,
)

export const selectIsClickedColumn = createSelector(
    selectClickedColumn,
    selectColumnId,
    (clickedColumn, columnId) => clickedColumn === columnId,
)

// if the column and row are both considered clicked, then this cell is the clicked cell
export const selectIsClickedCell = createSelector(
    selectIsClickedRow,
    selectIsClickedColumn,
    (isRowClicked, isColumnClicked) => isRowClicked && isColumnClicked,
)

export const selectIsEditing = createSelector(
    selectColumnIsEditable,
    selectIsClickedCell,
    (columnIsEditable, isClickedCell) => columnIsEditable && isClickedCell,
)

//----------- END UI Selectors ------------- //

// get the value to display in a particular cell, depending on if we are rendering a ResourceTable
// or a Sheet
export const selectCellValue = createSelector(
    selectIsResourceTable,
    selectColumnField,
    selectRowForCell,
    selectRowIndexFromProps,
    selectRowIdForCell,
    selectSheetId,
    selectColumnData,
    selectResourceTableOptionalActionCreator,
    selectResourceTableOptionalDisplayFunction,
    selectColumnType,
    selectSheetDataType,
    selectColumnIndexFromProps,
    selectTagTypeForCustomTagCell,
    (
        isResourceTable,
        columnField,
        row,
        rowIndex,
        rowId,
        sheetKey,
        columnData,
        optionalActionCreator,
        optionalDisplayFunc,
        columnType,
        dataType,
        columnIndex,
        tagType,
    ) => {
        if (isResourceTable) {
            // make sure we have received data and are ready to get the cell value
            if (!columnField) {
                return
            }

            return processCellValue({
                columnField,
                columnIndex,
                display: optionalDisplayFunc,
                optionalActionCreator,
                row,
                rowIndex,
                sheetKey,
            })
        } else {
            const value = columnData && columnData.size ? columnData.get(String(rowId)) : null

            // if the values are boolean values, we want to return their values as is
            if (tagType === DjangIO.app.person.types.TagType.boolean.value) {
                return value
            }

            return value === null || value === undefined
                ? getNullValueForColumn(dataType, columnType, columnField)
                : value
        }
    },
)

// ------------------ BEGIN number counter cell selectors -------------------------- //

export const selectValue = (_, props) => props.value
export const selectDisplayFunc = (_, props) => props.displayFunc

/**
 * This selector processes a columnField from a list of rows and returns
 * a list of their values in sorted order
 * @name selectSortedColumnValues
 * @function
 * @returns {Array<Number>} - values for a single column in increasing order
 */
export const selectSortedColumnValues = createSelector(
    selectColumnField,
    selectFilteredAndSortedDataForComponent,
    selectDisplayFunc,
    (columnField, rowData, display) => {
        if (!rowData) {
            return []
        }
        const set = new Set()
        if (columnField instanceof Immutable.List) {
            const columnData = rowData.map((row) => {
                const value = display(...columnField.map((field) => row[field]))
                set.add(value)
            })
            return [...set].sort((a, b) => a - b)
        }
        const columnData = rowData.map((row) => set.add(row[columnField]))
        return [...set].sort((a, b) => a - b)
    },
)

/**
 * This selector determines the opacity of a NumberCounter by grabbing the current
 * value and determining its position relative to the other values in its column.
 * The constant CIRCLE_COUNTER_MIN_OPACITY is used to set the default minimum opacity
 * since a very low opacity circle will not be visible for users.
 *
 * @name selectCircleCounterOpacity
 * @function
 * @returns {Number} - the opacity of the circle from range [0, 1]
 */
export const selectCircleCounterOpacity = createSelector(selectSortedColumnValues, selectValue, (columnList, value) => {
    if (columnList.length <= 1) {
        return value === 0 ? sheetConstants.CIRCLE_COUNTER_MIN_OPACITY : 1
    }
    const rawOpacity = columnList.indexOf(value) / (columnList.length - 1)
    return rawOpacity * (1 - sheetConstants.CIRCLE_COUNTER_MIN_OPACITY) + sheetConstants.CIRCLE_COUNTER_MIN_OPACITY
})

// ------------------ END number counter cell selectors -------------------------- //

export const selectResourceTableMultipleFields = createSelector(
    selectIsResourceTable,
    selectColumnField,
    (isResourceTable, columnField) => isResourceTable && columnField && columnField.constructor === Immutable.List,
)

// whether or not we should render a sorting indicator on the HeaderCell
export const selectCanSort = createSelector(
    selectIsResourceTable,
    selectResourceTableMultipleFields,
    makeColumnPropertySelector("resourceTableEnableSorting"),
    selectSheetCanFitInMemory,
    selectCanSortOnBackend,
    selectResourceTableSortOn,
    selectColumnSortingQuerymethodFromColumn,
    selectIsDatumColumn,
    makeResourceTableConfigSelector("datumColumnSortingQuerymethod"),
    (
        isResourceTable,
        resourceTableMultipleFields,
        resourceTableEnableSorting,
        sheetCanFitInMemory,
        canSortOnBackend,
        resourceTableSortOn,
        columnSortingQuerymethod,
        isDatumColumn,
        datumColumnSortingQuerymethod,
    ) =>
        isResourceTable
            ? Boolean(
                  ((!resourceTableMultipleFields || resourceTableSortOn || columnSortingQuerymethod) &&
                      resourceTableEnableSorting) ||
                      (isDatumColumn && datumColumnSortingQuerymethod),
              )
            : sheetCanFitInMemory || canSortOnBackend,
)

// whether or not we should render a searching indicator in the HeaderCell
export const selectCanSearch = createSelector(
    selectIsResourceTable,
    selectResourceTableMultipleFields,
    makeColumnPropertySelector("resourceTableEnableFiltering"),
    selectResourceTableFilterOn,
    selectSheetCanFitInMemory,
    selectCanFilterOnBackend,
    selectColumnType,
    (
        isResourceTable,
        resourceTableMultipleFields,
        resourceTableEnableFiltering,
        resourceTableFilterOn,
        sheetCanFitInMemory,
        canSortOnBackend,
        columnType,
    ) =>
        isResourceTable
            ? (!resourceTableMultipleFields || resourceTableFilterOn) && resourceTableEnableFiltering
            : (sheetCanFitInMemory || canSortOnBackend) &&
              columnType !== DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value,
)

/**
 * determine the number of buttons to render in HeaderCells
 * We use isResourceTable to determine a case in Sheets where we actually want to show the sort/search buttons,
 * but disable them if canSort/canSearch are false. Otherwise, we don't want to render those buttons
 * This selector helps determine how wide the ColumnSearchTrigger is
 * @name selectNumberOfButtons
 * @function
 * @param {Type} argument - a description
 * @returns {Type} - Something
 */
export const selectNumberOfButtons = createSelector(
    selectCanAddColumns,
    selectCanHideColumns,
    selectCanRemoveColumns,
    selectCanSearch,
    selectCanSort,
    selectCanCustomColorColumn,
    selectIsResourceTable,
    selectIsPublicSheetMode,
    (
        canAddColumns,
        canHideColumns,
        canRemoveColumns,
        canSearch,
        canSort,
        canColor,
        isResourceTable,
        isPublicSheetMode,
    ) =>
        [
            canAddColumns,
            canHideColumns,
            canRemoveColumns,
            // do not show the edit colors button on any resource table or public sheet
            canColor && !isResourceTable && !isPublicSheetMode,
            canSearch || !isResourceTable,
            canSort || !isResourceTable,
        ].reduce((acc, showButton) => (showButton ? acc + 1 : acc), 0),
)

export const selectNumberDatumHeaderCellButtons = createSelector(
    selectCanAddColumns,
    selectCanHideColumns,
    selectCanRemoveColumns,
    selectCanResizeRows,
    (canAddColumns, canHideColumns, canRemoveColumns, canResizeRows) =>
        [canAddColumns, canHideColumns, canRemoveColumns, canResizeRows, canResizeRows, true].reduce(
            (acc, showButton) => (showButton ? acc + 1 : acc),
            0,
        ),
)

export const selectDatumCellTotalColumnIconsWidth = createSelector(
    selectNumberDatumHeaderCellButtons,
    (numberOfButtons) => numberOfButtons * (sheetsVariables.columnIconsSize + sheetsVariables.columnIconsMargin) + 4,
)

// compute the width the icons need to take up
export const selectTotalColumnIconsWidth = createSelector(
    selectNumberOfButtons,
    (numberOfButtons) => numberOfButtons * (sheetsVariables.columnIconsSize + sheetsVariables.columnIconsMargin) + 4,
)

export const selectHeaderCellStyles = createSelector(
    selectTotalColumnIconsWidth,
    selectIsColumnSearching,
    selectIsColumnSorting,
    selectColumnHasError,
    (totalColumnsIconsWidth, isSearching, isSorting, columnHasError) => {
        return {
            columnIcons: encodeStyles({
                width: totalColumnsIconsWidth + 1,
            }),
            columnOptions: encodeStyles({
                ...(columnHasError
                    ? {
                          pointerEvents: "none",
                      }
                    : hideIfTrue(isSearching || isSorting)),
                right: -1 * totalColumnsIconsWidth,
            }),
        }
    },
)

export const selectDatumHeaderCellStyles = createSelector(
    selectDatumCellTotalColumnIconsWidth,
    selectDatumColumnIsSearching,
    (totalColumnsIconsWidth, isSearching) => ({
        columnIcons: encodeStyles({
            width: totalColumnsIconsWidth + 1,
        }),
        columnOptions: encodeStyles({
            ...hideIfTrue(isSearching),
            right: -1 * totalColumnsIconsWidth,
        }),
    }),
)

// Get the title for the DatumHeaderCell
// default can be overriden by ResourceTable configuration
export const selectDatumHeaderCellTitle = createSelector(
    selectIsResourceTable,
    selectSheetDataType,
    makeResourceTableConfigSelector("datumHeaderCellTitle"),
    (isResourceTable, sheetDataType, datumHeaderCellTitle) => {
        if (isResourceTable && datumHeaderCellTitle) {
            return datumHeaderCellTitle
        } else {
            if (
                sheetDataType === DjangIO.app.models.QuorumDataType.person.value &&
                checkHasRegion(allRegionsExceptUS)
            ) {
                return "Official / Staffer"
            }
            return DjangIO.app.models.QuorumDataType.by_value(sheetDataType).singular
        }
    },
)

export const selectTooltipTextForDatumHeaderCell = createSelector(
    selectIsResourceTable,
    makeResourceTableConfigSelector("resourceTableTooltipText"),
    (isResourceTable, resourceTableTooltipText) => (isResourceTable ? resourceTableTooltipText : ""),
)

/**
 * This selector determines how to interpret the custom color settings for a cell
 * @name selectCustomColorConditionTypeForCell
 * @function
 * @param {Number} columnDataType - the data type of the column
 * @param {Number|} tagType - if the cell belongs to a CustomTagColumn, the custom tag type
 * @param {Object} fieldObject - the field object for this column
 * @returns {String} - the type of rule interpretation that should be used
 */
export const selectCustomColorConditionTypeForCell = createSelector(
    selectColumnDataType,
    selectTagTypeForCustomTagCell,
    selectFieldObject,
    (columnDataType, tagType, fieldObject) => {
        // if a custom field, switch on the tagType instead of the columnDataType
        // custom tags pass their slugs as the cellValues
        if (tagType) {
            switch (tagType) {
                case DjangIO.app.person.types.TagType.single_option_list.value:
                case DjangIO.app.person.types.TagType.string.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.EXACT

                case DjangIO.app.person.types.TagType.number.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.NUMERIC
                default:
                    // multi_option_list is unsupported because the behavior cannot be determined
                    return undefined
            }
        } else if (fieldObject && Object.keys(fieldObject).length) {
            // since enums are stored as integers, we need to make sure we check
            // for enums first, as we don't want the coloring to be interpreted as numbers
            // as that would introduce a range rather than an exact match
            if (fieldObject._is_enum_field) {
                return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.EXACT
            }
            switch (fieldObject.field_type) {
                case DjangIO.djangio.types.FieldType.CharField.value:
                case DjangIO.djangio.types.FieldType.TextField.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.EXACT
                case DjangIO.djangio.types.FieldType.IntegerField.value:
                case DjangIO.djangio.types.FieldType.FloatField.value:
                case DjangIO.djangio.types.FieldType.DecimalField.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.NUMERIC
                case DjangIO.djangio.types.FieldType.DateField.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.DATE
                default:
                    return undefined
            }
        } else {
            switch (columnDataType) {
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.EXACT

                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.NUMERIC
                case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
                    return sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.DATE
                default:
                    return undefined
            }
        }
    },
)

/**
 * This selector determines the custom coloring for a cell if applicable
 * @name selectCustomColoringForCell
 * @function
 * @param {Boolean} shouldCustomColor - whether or not we should apply custom coloring to this cell
 * @param {Map} customColorSettings - the custom color mapping for a particular cell
 * @param {Number|String|} cellValue - the computed cell value for a cell
 * @param {String} conditionType - one of the color form condition constants indicating which condition rules to apply
 * @returns {Type} - Something
 */
export const selectCustomColoringForCell = createSelector(
    selectShouldCustomColorColumn,
    selectCustomColorSettings,
    selectCellValue,
    selectCustomColorConditionTypeForCell,
    selectCustomTag,
    selectColumnType,
    selectColumnData,
    selectRowIdForCell,
    (shouldCustomColor, customColorSettings, cellValue, conditionType, customTag, columnType, columnData, rowId) => {
        if (!shouldCustomColor) {
            return undefined
        }

        const value = columnData && columnData.size ? columnData.get(String(rowId)) : null
        let cellVal = cellValue
        if (columnType === DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value) {
            cellVal = customTag.getIn(["options_new", value, "value"], value)
        }
        switch (conditionType) {
            case sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.EXACT:
                const valToUse = sheetConstants.FALSEY_VALUES.includes(cellVal) ? "" : String(cellVal)
                return customColorSettings.get(cellVal) || customColorSettings.get(valToUse)
            case sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.NUMERIC:
                return computeLeftBoundRange({
                    mapping: customColorSettings,
                    value: sheetConstants.FALSEY_VALUES.includes(cellVal) ? "" : Number(cellVal),
                })
            case sheetConstants.CUSTOM_COLOR_CONDITION_TYPE.DATE:
                return computeLeftBoundRange({
                    mapping: customColorSettings, // function handles transforming to moment
                    value: cellVal,
                    comparingDates: true,
                })
            default:
                return undefined
        }
    },
)

// grabs the 'state' string of a specific cell, under store:sheet:sheetId:cellStates (via selectCellStates) -> :rowId:columnId
export const selectStateForCell = createSelector(
    selectCellStates,
    selectRowIdForCell,
    selectColumnId,
    (states, rowId, columnId) => {
        // Immutable allows integers as keys in its maps, while vanilla js yields the same if obj[1] and obj['1']
        return states && states.getIn([String(rowId), String(columnId)])
    },
)

export const selectCellValueIsNotEditable = createSelector(selectCellValue, (cellValue) => cellValue === "Not Editable")

export const selectCellIsEditable = createSelector(
    selectSheetDataType,
    selectColumnIsEditable,
    selectUserId,
    selectIsOrgAdmin,
    selectShouldLimitEditingNotesToAdminsAndCreators,
    selectCellValue,
    selectCellValueIsNotEditable,
    selectColumnType,
    selectFieldObject,
    (_, props) => props.datum,
    (qdt, columnIsEditable, userId, orgAdmin, shouldLimit, value, valueIsNotEditable, columnType, fieldObj, datum) => {
        if (columnIsEditable && qdt === DjangIO.app.models.QuorumDataType.note.value && shouldLimit && !orgAdmin) {
            return datum.get("user_id") === userId
        }

        const PAC_CHARITY_SHEET_COLUMNS = [
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_charity_additional_info.value,
            DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_charity_anonymous.value,
        ]
        if (columnIsEditable && qdt === DjangIO.app.models.QuorumDataType.supporter.value) {
            if (valueIsNotEditable && PAC_CHARITY_SHEET_COLUMNS.includes(columnType)) {
                return false
            }
            if (!value && fieldObj.name === "pac_charity_selection_date") {
                return false
            }
        }
        return columnIsEditable
    },
)

// responsible for mapping states to styles. has a case for error cells, but also takes in the cellStateStyleMap
// returning an obj of styles (to be directly applies under {style=} for SheetCell (consumer of cellConnection))
export const selectStateStyleForCell = createSelector(
    selectStateForCell,
    selectCellStateToStyleProp,
    selectCellIsEditable,
    selectCustomColoringForCell,
    selectIsResourceTable,
    (state, props) => props.isMantineSheet,
    (state, styles, columnIsEditable, customColoringForCell, isResourceTable, isMantineSheet) => {
        if (styles && styles.get(state)) {
            return styles.get(state).toJS()
        } else if (state === "error") {
            return {
                backgroundColor: sheetConstants.CELL_HAS_ERROR_COLOR,
            }
        } else if (customColoringForCell) {
            // custom coloring overrides editable styling
            return {
                backgroundColor: customColoringForCell,
            }
        } else if (columnIsEditable && !isResourceTable && !isMantineSheet) {
            // We're using different colors in the new Mantine based Sheet and keeping that logic elsewhere
            return {
                backgroundColor: sheetConstants.CELL_IS_EDITABLE_COLOR,
            }
        }
        return {}
    },
)

export const selectStateStyleForMantineCell = createSelector(
    selectStateForCell,
    selectCellStateToStyleProp,
    selectCellIsEditable,
    selectCustomColoringForCell,
    selectIsResourceTable,
    (state, props) => props.isMantineSheet,
    (state, styles, columnIsEditable, customColoringForCell, isResourceTable, isMantineSheet) => {
        const style = {
            padding: ".25rem .5rem",
        }

        if (styles && styles.get(state)) {
            return styles.get(state).toJS()
        } else if (state === "error") {
            style.backgroundColor = sheetConstants.CELL_HAS_ERROR_COLOR
        } else if (customColoringForCell) {
            // custom coloring overrides editable styling
            style.backgroundColor = customColoringForCell
        } else if (columnIsEditable && !isResourceTable && !isMantineSheet) {
            // We're using different colors in the new Mantine based Sheet and keeping that logic elsewhere
            style.backgroundColor = sheetConstants.CELL_IS_EDITABLE_COLOR
        }

        return style
    },
)

export const selectParentRowIndexes = createSelector(
    selectRowData,
    (rowData) =>
        rowData &&
        rowData
            .toJS()
            .map((each, index) => ({ parent: !!each.parent, index }))
            .filter((each) => !each.parent)
            .map((each) => each.index),
)

export const selectParentOddOrEven = createSelector(
    selectParentRowIndexes,
    selectRowIndexFromProps,
    (parentIndexes, rowIndex) => {
        if (parentIndexes && parentIndexes.length) {
            rowIndex = parentIndexes.findIndex((each) => each > rowIndex)
            rowIndex = rowIndex < 0 ? parentIndexes.length - 1 : rowIndex - 1
        }
        return rowIndex % 2
    },
)

export const selectIsSortingOrSearching = createSelector(
    selectSortingColumnIndex,
    selectSearchingColumnIndexes,
    (sortingIndex, searchingIndexes) =>
        Boolean(sortingIndex || sortingIndex === 0 || (searchingIndexes && searchingIndexes.size)),
)

const selectSavedSearchData = makeColumnPropertySelector("source_saved_search_data")

export const selectSavedSearchAddedIds = createSelector(
    selectSavedSearchData,
    (savedSearch) => savedSearch && savedSearch.get("added_ids").toJS(),
)

export const selectSavedSearchDeletedIds = createSelector(
    selectSavedSearchData,
    (savedSearch) => savedSearch && savedSearch.get("deleted_ids").toJS(),
)

export const selectSavedSearchFilters = createSelector(
    selectSavedSearchData,
    (savedSearch) => savedSearch && savedSearch.get("filters").toJS(),
)

// Get the names from the issue objects returned from the backend
// and convert them to the value the pie select should display
export const selectDisplayValuesForIssueCell = (state, props) => {
    return (
        props.children &&
        // return a comma separated string of the names of
        // all the issues that are associated with this row
        props.children
            .map((issue) => [issue.get ? issue.get("name") : undefined].join(" ").trim())
            .filter((value) => !!value)
            .join(", ")
    )
}

// Get the ids from the issue objects returned from the backend
// for use by the pie select
export const selectIdsForIssueCell = (state, props) => {
    return (
        props.children &&
        props.children
            .map((issue) =>
                // need to return undefined because default is null which is
                // an object (causes problems later on when the initial value)
                // is passed into the PieSelect
                issue.get ? issue.get("id") : undefined,
            )
            .filter((value) => !!value)
            .toJS()
    )
}

export const allowSegueToSearch = createSelector(
    selectSavedSearchData,
    selectIsPublicSheetMode,
    (savedSearch, isPublicSheet) => {
        if (isPublicSheet) {
            return false
        }
        return Boolean(savedSearch && savedSearch.get("added_ids") ? !savedSearch.get("added_ids").size : true)
    },
)

export const selectSearchDataType = createSelector(
    selectSavedSearchData,
    (savedSearch) => savedSearch && DjangIO.app.models.QuorumDataType.by_value(savedSearch.get("data_type")),
)

const selectSearchDataTypePlural = createSelector(
    selectSearchDataType,
    (searchDataType) => searchDataType && searchDataType.plural,
)

export const selectSegueToSearchArgs = createSelector(
    selectColumnType,
    selectSheetDataType,
    selectSavedSearchData,
    (_, props) => props.rowId,
    (columnType, dataType, savedSearch, rowId) => {
        if (!savedSearch) {
            return
        }
        const quorumDataType = savedSearch.get("data_type")
        let initialFilter = savedSearch.get("filters") ? savedSearch.get("filters").toJS() : []
        if (initialFilter.constructor === Array) {
            initialFilter = initialFilter.reduce((a, b) => Object.assign(a, b), {})
        }
        initialFilter.deleted_ids = savedSearch.get("deleted_ids") && savedSearch.get("deleted_ids").toJS()
        const searchFilter =
            columnType && DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).search_filters
        if (searchFilter) {
            const key = searchFilter.any || searchFilter[dataType]
            initialFilter[key] = rowId
        }
        return { initialFilter, quorumDataType }
    },
)

const selectIsChildAndNotSortingOrSearching = createSelector(
    (_, props) => props.datum,
    selectIsSortingOrSearching,
    (datum, isSortingOrSearching) => !isSortingOrSearching && datum.get("parent"),
)

const selectChildDepth = createSelector(
    (_, props) => props.datum,
    (datum) => {
        if (datum.get("parent")) {
            return datum.getIn(["_extra", "ancestor_ids"]) ? datum.getIn(["_extra", "ancestor_ids"]).size - 1 : 0
        }
        return 0
    },
)

export const selectIsOddCell = createSelector(
    selectParentOddOrEven,
    selectIsSortingOrSearching,
    (parentOddOrEven, isSortingOrSearching) => !isSortingOrSearching && !parentOddOrEven,
)

export const selectIsEvenCell = createSelector(
    selectParentOddOrEven,
    selectIsSortingOrSearching,
    (parentOddOrEven, isSortingOrSearching) => !isSortingOrSearching && parentOddOrEven,
)

export const selectIsIndexOddCell = createSelector(
    selectIsOddCell,
    (_, props) => props.usesParentChildCell,
    (isOddCell, usesParentChildCell) => isOddCell && usesParentChildCell,
)

export const selectIsIndexEvenCell = createSelector(
    selectIsEvenCell,
    (_, props) => props.usesParentChildCell,
    (isEvenCell, usesParentChildCell) => isEvenCell && usesParentChildCell,
)

/**
 * This selector is applicable for columns in Sheets that use FieldOptionType to configure the field
 * that they should display
 * @name selectFieldOptionTypeDisplayKey
 * @function
 * @param {Number} columnType - a member of the SheetColumnTypeEnum
 * @returns {String|undefined} - the display key of the enum, if the enum exists. If not applicable undefined
 */
export const selectFieldOptionTypeDisplayKey = createSelector(selectColumnType, (columnType) => {
    try {
        return DjangIO.app.userdata.spreadsheet.types.FieldOptionType.by_value(columnType).enum_field
    } catch (e) {
        return undefined
    }
})

/**
 * This selector returns the enumLabel to use for an Enum column.
 * If not provided, Sheets defaults to "label" as the enbum access key
 * @name selectResourceTableOptionalEnumColumnLabel
 * @function
 * @param {Object} column - a column configuration object
 * @returns {String|undefined} - Either the supplied enumLabel, or undefined
 */
export const selectResourceTableOptionalEnumColumnLabel = createSelector(selectPropFromProps("column"), (column) => {
    if (column && column.enumLabel) {
        return column.enumLabel
    } else {
        return undefined
    }
})

/*
 * This selector determines the appropriate display key to use for
 * @name selectDisplayKeyForEnumColumn
 * @function
 * @param {Object} fieldObj - a DjangIO representation of a field
 * @param {String} fieldOptionTypeDisplayKey - a String represented a custom enum key
 * @param {Boolean} isResourceTable - Whether or not this is a resource table
 * @returns {Type} - String
 */
export const selectDisplayKeyForEnumColumn = createSelector(
    selectFieldObject,
    selectFieldOptionTypeDisplayKey,
    selectIsResourceTable,
    selectResourceTableOptionalEnumColumnLabel,
    (fieldObj, fieldOptionTypeDisplayKey, isResourceTable, resourceTableOptionalEnumColumnLabel) => {
        // This case is for Sheet columns that use FieldOptionType
        if (fieldOptionTypeDisplayKey) {
            return fieldOptionTypeDisplayKey
        }

        // allow for a customizable display key, set from the PublicField wrapper on the backend
        // or from FieldOptionType in spreadsheet/types.py
        else if (fieldObj.sheets_display_key) {
            return fieldObj.sheets_display_key
        }
        if (isResourceTable && resourceTableOptionalEnumColumnLabel) {
            return resourceTableOptionalEnumColumnLabel
        }

        // the default key to access enums with
        return "label"
    },
)

// LargeTextCell selectors

/**
 * This selector determines the width of the text
 * @name selectTextWidth
 * @function
 * @param {String|Object} children - Either the text in the cell, or a React component / html.
 * Html can be passed in the case of a MentionableCell
 * @returns {Integer} - An estimated width of the text
 */
export const selectTextWidth = createSelector(
    (_, props) => props.children,
    (children) => {
        if (!children) {
            return 0
        } else if (typeof children === "string") {
            // TODO sheetConstants.columnNameFont no longer exists. Fix this and probably fix the fudge factor
            return calculateTextWidth(sheetConstants.columnNameFont, children)
        } else if (typeof children === "object" && children.props) {
            // We may have been passed a React component. In this case, we support checking the length of the html
            const html = getNestedObjectKey(children, ["props", "dangerouslySetInnerHTML", "__html"])
            if (html) {
                return calculateTextWidth(sheetConstants.columnNameFont, children.props.dangerouslySetInnerHTML.__html)
            } else {
                console.warn("Could not find a string to compute the length for in this React component")
                return 0
            }
        } else {
            console.warn(`Received an incompatible children type of ${typeof children}`)
        }
        return 0
    },
)

export const selectShouldRenderTooltip = createSelector(
    selectTextWidth,
    (_, props) => props.width,
    (_, props) => props.rowHeightFactor,
    (textWidth, columnWidth, rowHeightFactor) =>
        textWidth * sheetConstants.TOOLTIP_SHOW_FUDGE_FACTOR > columnWidth * (2 + (2 * rowHeightFactor - 2)),
)

/**
 * Provide the cell link (url to segue to for a given cell) if the cell link is valid
 * and the cell is non editable, from the resource table, and the individual cell
 * does not contain the prop shouldDisableRowLink. (refer to ActionColumnGrid for an example)
 *
 * NOTE: if disableRowLink is passed as true to ResourceTableContainer then cellLink will
 * be undefined due to the logic in selectCellLink
 *
 * @return {String} url string if sheet cell link is valid, else undefined
 */
export const selectSheetCellLink = createSelector(
    selectCellLink,
    selectResourceTableDisableCellLink,
    selectIsResourceTable,
    selectCellValue,
    selectResourceTableEnableEditing,
    (cellLink, disableLink, isResourceTable, cellValue, enableEditing) => {
        if (enableEditing || !isResourceTable || disableLink) {
            return undefined
        }
        if (cellValue && cellValue.props && cellValue.props.shouldDisableRowLink) {
            return undefined
        }
        return cellLink
    },
)

/**
 * Selector that determins if the cell should be a stat cell
 *
 * The result depends whether or not the column is a field column.  If it is we use the column's FieldType from the
 * field object. Otherwise we'll look at the ColumnDataType from the SheetColumnType.
 *
 * Note: we don't check the ColumnDataType on Field Columns becuase the DataType is a will be ColumnCategory.field and this
 * value will not determine if the column has number values.  Instead we'll look at it's FieldType (if it's not an enum)
 *
 * @returns {Boolean}
 */
export const selectIsStatCell = createSelector(
    selectColumnDataType,
    selectFieldObject,
    selectColumnType,
    (columnDataType, field, columnType) => {
        if (columnType === DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value) {
            return false
        } else if (field && field._is_enum_field) {
            return false
        } else if (field && field.field_type) {
            const fieldTypeValue = field.field_type
            return DjangIO.djangio.types.FieldType.by_value(fieldTypeValue).should_show_column_math
        } else if (columnDataType) {
            return DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.by_value(columnDataType)
                .should_show_column_math
        } else {
            return false
        }
    },
)

/**
 * @returns {Number} - the number of rows in the Sheet
 */
export const selectRowCount = createSelector(selectRowCountFromProps, (rowCount) => rowCount)

/**
 * Converts a list of filtered and sorted rows to a map for quick lookups.
 *
 * @returns { Map<Number, Boolean> } - returns a map of the row id and true if it exists
 */
export const selectRowDataDict = createSelector(selectFilteredAndSortedRowData, (data) =>
    data
        ? data.reduce((result, item) => {
              return result.set(item.get("id"), true)
          }, Immutable.Map())
        : Immutable.Map(),
)

/**
 * Since the columnData has the values we want, and selectRowDataDict has the rows we want,
 * we use the rowDataDict to filtered out the columnData that we do not want
 *
 * @returns { Map<Number, any> } - k: row id,  v: filtered column data
 */
export const selectFilteredColumnData = createSelector(
    selectRowDataDict,
    selectColumnData,
    (rowDataDict, columnData) => {
        return columnData ? columnData.filter((_, id) => rowDataDict.get(Number(id))) : Immutable.Map()
    },
)

/**
 *
 * Uses selectFilteredColumnData to calculate the aggregate sum of the row values in a column
 *
 * @returns {Number | undefined} - returns the aggregate value of the column if there is column data
 */
export const selectSheetsFooterMathValue = createSelector(
    selectFilteredColumnData,
    selectColumnSum,
    (filteredColumnData, customDataSum) => customDataSum ?? filteredColumnData?.reduce((a, b) => Number(a) + Number(b)),
)

/**
 * Determins the text value the MathFooter will render
 *
 * @returns { String | undefined } the value of the footer cell
 */
export const selectFooterLabel = createSelector(
    selectIsIndexColumn,
    selectIsDatumColumn,
    selectRowCount,
    selectIsStatCell,
    selectSheetsFooterMathValue,
    selectColumnDataType,
    (isIndexColumn, isDatumColumn, rowCount, isStatCell, sheetsFooterValue, columnDataType) => {
        if (isIndexColumn) {
            return undefined
        }
        if (isDatumColumn) {
            const formattedCount = new Intl.NumberFormat().format(rowCount)
            return `Count: ${formattedCount}`
        }
        if (isStatCell && sheetsFooterValue) {
            let formattedSum
            if (columnDataType === DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value) {
                formattedSum = centsToCurrencyStr(sheetsFooterValue)
            } else {
                formattedSum = new Intl.NumberFormat().format(sheetsFooterValue)
            }
            return `Sum: ${formattedSum}`
        }
        return undefined
    },
)

export const footerConnection = createStructuredSelector({
    footerLabel: selectFooterLabel,
    shouldDisplayMathBar: selectShouldDisplayMathBar,
})

export const searchStatCellConnection = createStructuredSelector({
    allowSegueToSearch,
    searchDataType: selectSearchDataTypePlural,
    segueToSearchArgs: selectSegueToSearchArgs,
})

export const datumHeaderCellConnection = createStructuredSelector({
    canAddColumns: selectCanAddColumns,
    canHideColumns: selectCanHideColumns,
    canResizeRows: selectCanResizeRows,
    canSetDefaultSort: selectCanSetDefaultSort,
    canSort: selectCanSort,
    columnDataType: selectColumnDataType,
    dataType: selectSheetDataType,
    datumCellStyles: selectDatumHeaderCellStyles,
    datumHeaderTitle: selectDatumHeaderCellTitle,
    defaultSortingType: selectDefaultSortColumnType,
    isDefaultSorting: selectIsColumnDefaultSorting,
    isResourceTable: selectIsResourceTable,
    loading: selectSheetIsLoading,
    rowHeight: selectRowHeight,
    rowHeightFactor: selectRowHeightFactor,
    searching: selectDatumColumnIsSearching,
    shouldShowDatumColumn: showDatumColumn,
    sortType: selectSortType,
    sorting: selectIsColumnSorting,
    tooltipText: selectTooltipTextForDatumHeaderCell,
})

/** @type {(state?: any, props?: any) => any} */
export const tagOptionCellConnection = createStructuredSelector({
    allCustomFieldOptionsForSelect: selectAllCustomFieldOptionsForSelect,
    customFieldOptions: selectCustomFieldOptions,
    customFieldOptionsForDisplay: selectCustomFieldOptionsForDisplay,
})

/** @type {(state?: any, props?: any) => any} */
export const indexCellConnection = createStructuredSelector({
    datumId: selectDatumId,
    isResourceTable: selectIsResourceTable,
    swalConfig: selectResourceTableSwalConfig,
    rowResource: makeResourceTableConfigSelector("rowResource"),
})

/** @type {(state?: any, props?: any) => any} */
export const enumCellConnection = createStructuredSelector({
    displayKey: selectDisplayKeyForEnumColumn,
    isResourceTable: selectIsResourceTable,
    transformFunction: selectTransformFunctionForDisplayCell,
})

/** @type {(state?: any, props?: any) => any} */
export const headerCellConnection = createStructuredSelector({
    canAddColumns: selectCanAddColumns,
    canFilterOnBackend: selectCanFilterOnBackend,
    canFitInMemory: selectSheetCanFitInMemory,
    canHideColumns: selectCanHideColumns,
    canRenameColumns: selectCanRenameColumns,
    canRemoveColumns: selectCanRemoveColumns,
    canSearch: selectCanSearch,
    canSort: selectCanSort,
    canSortOnBackend: selectCanSortOnBackend,
    canSetDefaultSort: selectCanSetDefaultSort,
    columnData: selectColumnDataForColumn,
    columnDataType: selectColumnDataType,
    columnField: selectColumnField,
    columnCustomErrorMessage: selectColumnCustomErrorMessage,
    columnHasError: selectColumnHasError,
    columnId: selectColumnId,
    columnName: selectColumnName,
    columnNameTooltip: selectColumnNameTooltip,
    columnType: selectColumnType,
    columnWidth: makeColumnPropertySelector("width", sheetConstants.DEFAULT_COLUMN_WIDTH),
    width: makeColumnPropertySelector("width", sheetConstants.DEFAULT_COLUMN_WIDTH),
    customTag: selectCustomTag,
    field: selectFieldObject,
    focused: selectIsColumnFocused,
    headerCellStyles: selectHeaderCellStyles,
    isDefaultSortingAndCanSetDefaultSort: selectIsDefaultSortingAndCanSetDefaultSort,
    isHidden: makeColumnPropertySelector("should_hide_column_on_public", false),
    isResizable: selectIsResizable,
    isResourceTable: selectIsResourceTable,
    isReorderable: selectIsReorderable,
    // the user can elect to make a particular column have a default sorting on public sheets
    defaultSortType: makeColumnPropertySelector("default_sort_type"),
    isSearching: selectIsColumnSearching,
    resourceTableMultipleFields: selectResourceTableMultipleFields,
    resourceTableFilterOn: selectResourceTableFilterOn,
    sorting: selectIsColumnSorting,
    isDefaultSorting: selectIsColumnDefaultSorting,
    defaultSortingType: selectDefaultSortColumnType,
    sortType: selectSortType,
    shouldDisplayAsCurrency: selectShouldDisplayAsCurrency,
    shouldRoundCellValue: selectShouldRoundCellValue,
    canCustomColorColumn: selectCanCustomColorColumn,
    isPublicSheetMode: selectIsPublicSheetMode,
})

/** @type {(state?: any, props?: any) => any} */
export const OrderingCellConnection = createStructuredSelector({
    rowId: selectRowIdForCell,
    id: selectSheetId,
    index: selectRowIndexFromProps,
    editing: selectIsEditing,
    isClickedRow: selectIsClickedRow,
    isClickedColumn: selectIsClickedColumn,
    numberOfRows: selectSheetRowsTotalCount,
})

/** @type {(state?: any, props?: any) => any} */
export const ParentChildCellConnection = createStructuredSelector({
    oddCell: selectIsOddCell,
    evenCell: selectIsEvenCell,
    childDepth: selectChildDepth,
    isChildAndNotSortingOrSearching: selectIsChildAndNotSortingOrSearching,
})

/** @type {(state?: any, props?: any) => any} */
export const largeTextCellConnection = createStructuredSelector({
    shouldRenderTooltip: selectShouldRenderTooltip,
    textWidth: selectTextWidth,
})

/** @type {(state?: any, props?: any) => any} */
export const issueSelectCellConnection = createStructuredSelector({
    savedSearchAddedIds: selectSavedSearchAddedIds,
    savedSearchDeletedIds: selectSavedSearchDeletedIds,
    savedSearchFilters: selectSavedSearchFilters,
    displayValues: selectDisplayValuesForIssueCell,
    selectIds: selectIdsForIssueCell,
})

// Get the names from the issue objects returned from the backend
// and convert them to the value the pie select should display
export const selectManinteDisplayValuesForIssueCell = (state, props) => {
    return props?.children
        ?.map((issue) => issue?.name?.trim?.())
        .filter((value) => !!value)
        .join(", ")
}
// Get the ids from the issue objects returned from the backend
// for use by the pie select
export const selectIdsMantineForIssueCell = (state, props) => {
    return props?.children?.map((issue) => issue.id).filter((value) => !!value)
}

/** @type {(state?: any, props?: any) => any} */
export const mantineIssueSelectCellConnection = createStructuredSelector({
    savedSearchAddedIds: selectSavedSearchAddedIds,
    savedSearchDeletedIds: selectSavedSearchDeletedIds,
    savedSearchFilters: selectSavedSearchFilters,
    displayValues: selectManinteDisplayValuesForIssueCell,
    selectIds: selectIdsMantineForIssueCell,
})

const selectWasCustomFieldFromNoteCellUpdatedPreemptively = (state, ownProps) => {
    // A CustomNoteFromFieldCell will get its value in one of two ways:
    //  1) A generate call which will be an immutableJS object of the form { value: _, exists: _ }
    //  2) The patchColumnData action creator which will preemptively send just the primitive value
    //     not wrapped in an object.
    return typeof ownProps.cellValue !== "object" || ownProps.cellValue === null
}

const selectTagTypeOfCustomFieldFromNoteCell = (state, ownProps) => {
    return selectTagTypeForCustomTagCell(state, { id: ownProps.sheetId, ...ownProps })
}

const selectCellValueOfCustomFieldFromNoteCell = (state, ownProps) => ownProps.cellValue

const selectNoteExistsForCustomFieldFromNoteCell = createSelector(
    selectWasCustomFieldFromNoteCellUpdatedPreemptively,
    selectCellValueOfCustomFieldFromNoteCell,
    (wasUpdatedPreemptively, cellValue) => wasUpdatedPreemptively || cellValue.get("exists"),
)

/** @type {(state?: any, props?: any) => any} */
const selectCustomDataValueOfCustomFieldFromNoteCell = createSelector(
    selectWasCustomFieldFromNoteCellUpdatedPreemptively,
    selectCellValueOfCustomFieldFromNoteCell,
    (wasUpdatedPreemptively, cellValue) => (wasUpdatedPreemptively ? cellValue : cellValue.get("value")),
)

/** @type {(state?: any, props?: any) => any} */
export const customFieldFromNoteCellConnection = createStructuredSelector({
    tagType: selectTagTypeOfCustomFieldFromNoteCell,
    cellExists: selectNoteExistsForCustomFieldFromNoteCell,
    customDataValue: selectCustomDataValueOfCustomFieldFromNoteCell,
})

// NOTE: a ImmutableJS Free selector to prevent issues in rendering custom tag cells. This is a holdover solution
// so we can establish E2E tests and mitigate risks and crashes. If you are reading this message half a year or more after
// it was committed, I am so, so sorry.
/** @type {(state?: any, props?: any) => any} */
export const mantineCustomFieldFromNoteCellConnection = createStructuredSelector({
    // this is technically immutable JS but is stable enough at the moment...
    tagType: selectTagTypeOfCustomFieldFromNoteCell,
    // consolidation of selectNoteExistsForCustomFieldFromNoteCell
    // and selectCustomDataValueOfCustomFieldFromNoteCell spaghetti
    cellExists: (state, ownProps) =>
        typeof ownProps.cellValue !== "object" || ownProps.cellValue === null || ownProps.cellValue?.exists,
    // consolidation of selectNoteExistsForCustomFieldFromNoteCell spaghetti
    customDataValue: (state, ownProps) =>
        typeof ownProps.cellValue !== "object" || ownProps.cellValue === null
            ? ownProps.cellValue
            : ownProps.cellValue.value,
})
